import {styled} from '@mui/material/styles';
import {Paper, TableRow, TableHead, Table, TableBody, TableCell, TableContainer, TableSortLabel} from '@mui/material';
import {type Plan, type Folder} from 'client';
import useParseURL from 'hooks/useParseURL';
import {URLParams, URLPathname} from 'interfaces';
import {useTranslation} from 'react-i18next';
import {FolderRow} from './PlanList/FolderRow';
import {PlanRow} from './PlanList/PlanRow';
import {useState} from 'react';
import {useGetNaturalComparator} from 'hooks/useGetNaturalComparator';

export function PlansTable({
  plans,
  folders,
  showFolders,
}: {
  plans: Plan[];
  folders: Folder[] | undefined;
  showFolders: boolean;
}) {
  const {t} = useTranslation('common');
  const {navigate, urlSearchParams, state} = useParseURL();
  const naturalComparator = useGetNaturalComparator();

  const navigateTo = (planId: string) => {
    urlSearchParams.append(URLParams.plan, planId);
    const pathname = URLPathname.planOverview;
    const from = state && state.from ? state.from.concat(pathname) : [pathname];
    navigate(
      {
        pathname,
        search: urlSearchParams.toString(),
      },
      {state: {from}},
    );
  };
  const [sortedBy, setSortedBy] = useState<keyof Plan | keyof Folder>('updatedAt');
  const [orderAsc, setOrderAsc] = useState<boolean>(false);
  const checkOrder = (order: boolean): 'desc' | 'asc' => (order ? 'asc' : 'desc');
  const comparePlansFn = buildCompareFn(sortedBy, checkOrder(orderAsc), naturalComparator);
  const compareFoldersFn = buildCompareFn(sortedBy, checkOrder(orderAsc), naturalComparator);
  const sortedPlans = [...plans].sort(comparePlansFn);
  const sortedFolders = folders && [...folders].sort(compareFoldersFn);

  return (
    <TableContainer component={Paper} elevation={0}>
      <Table stickyHeader data-testid="plans-table" size="small">
        <TableHead>
          <TableRow>
            <StyledTableCell
              key={'table-head-name'}
              onClick={() => {
                setOrderAsc(!orderAsc);
                setSortedBy('name');
              }}
            >
              <TableSortLabel active={sortedBy === 'name'} direction={checkOrder(orderAsc)}>
                {t('name')}
              </TableSortLabel>
            </StyledTableCell>
            <StyledTableCell
              key={'table-head-description'}
              onClick={() => {
                setOrderAsc(!orderAsc);
                setSortedBy('description');
              }}
            >
              <TableSortLabel active={sortedBy === 'description'} direction={checkOrder(orderAsc)}>
                {t('description')}
              </TableSortLabel>
            </StyledTableCell>
            <StyledTableCell
              key={'table-head-startdate'}
              onClick={() => {
                setOrderAsc(!orderAsc);
                setSortedBy('startDate');
              }}
            >
              <TableSortLabel
                active={sortedBy === 'startDate'}
                direction={checkOrder(orderAsc)}
                sx={{whiteSpace: 'nowrap'}}
              >
                {t('startDate')}
              </TableSortLabel>
            </StyledTableCell>
            <StyledTableCell
              key={'table-head-enddate'}
              onClick={() => {
                setOrderAsc(!orderAsc);
                setSortedBy('endDate');
              }}
            >
              <TableSortLabel
                active={sortedBy === 'endDate'}
                direction={checkOrder(orderAsc)}
                sx={{whiteSpace: 'nowrap'}}
              >
                {t('endDate')}
              </TableSortLabel>
            </StyledTableCell>
            <StyledTableCell
              key={'table-head-lastupdated'}
              onClick={() => {
                setOrderAsc(!orderAsc);
                setSortedBy('updatedAt');
              }}
            >
              <TableSortLabel active={sortedBy === 'updatedAt'} direction={checkOrder(orderAsc)}>
                {t('lastUpdated')}
              </TableSortLabel>
            </StyledTableCell>
            <StyledTableCell key={'table-head-inventory'}>{t('sync')}</StyledTableCell>
            <StyledTableCell key={'table-head-tags'}>{t('tags')}</StyledTableCell>
            <StyledTableCell></StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {showFolders ? (
            <>
              {sortedFolders &&
                sortedFolders.map((folder) => {
                  return (
                    <FolderRow key={folder.id} name={folder.name} folderId={folder.id}>
                      {sortedPlans.map(
                        (plan) =>
                          folder.id === plan.folderId && (
                            <PlanRow key={plan.id} plan={plan} navigate={navigateTo} nested={true} />
                          ),
                      )}
                    </FolderRow>
                  );
                })}
              {sortedPlans.map(
                (plan) => !plan.folderId && <PlanRow key={plan.id} plan={plan} navigate={navigateTo} nested={false} />,
              )}
            </>
          ) : (
            <>
              {sortedPlans.map((plan) => (
                <PlanRow key={plan.id} plan={plan} navigate={navigateTo} nested={false} />
              ))}
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const StyledTableCell = styled(TableCell)(({theme}) => ({
  backgroundColor: theme.palette.background.default,
}));

const buildCompareFn = (
  by: keyof Plan | keyof Folder,
  direction: 'asc' | 'desc',
  comparator: (x: string, y: string) => number,
) => {
  const isDescending = direction === 'desc';

  return (a: Plan | Folder, b: Plan | Folder) => {
    const hasPropertyA = by in a;
    const hasPropertyB = by in b;

    if (!hasPropertyA && !hasPropertyB) {
      return 0;
    }

    if (!hasPropertyA) {
      return isDescending ? 1 : -1;
    }
    if (!hasPropertyB) {
      return isDescending ? -1 : 1;
    }

    const valueA = a[by];
    const valueB = b[by];

    if (valueA == null && valueB == null) {
      return 0;
    }
    if (valueA == null) {
      return isDescending ? 1 : -1;
    }
    if (valueB == null) {
      return isDescending ? -1 : 1;
    }
    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return isDescending ? valueB - valueA : valueA - valueB;
    }
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      const dateA = Date.parse(valueA);
      const dateB = Date.parse(valueB);

      if (!isNaN(dateA) && !isNaN(dateB)) {
        return isDescending ? dateB - dateA : dateA - dateB;
      }

      const stringComparison = comparator(valueA, valueB);
      return isDescending ? -stringComparison : stringComparison;
    }

    return 0;
  };
};
